/**
 * Personalkosten: Stundenzeilen.
 * Aus pages/ProjektDetailPage.tsx herausgelöst (Plan Projekt-Tracking, Etappe 2) —
 * verschoben, nicht umgeschrieben.
 */
import { Fragment, useMemo, useState } from "react";
import { useProjektStunden, useUpdateSlot, useDeleteSlot, useBestaetigeStunden, useProjektPauschalen, useTeamMembers } from "../../hooks/usePlanung";
import type { Pauschale, StundenZeile } from "../../lib/types";
import { formatEur } from "../../lib/planer";
import { FILTER_LABEL, KostenZelle, NachtragForm, PersonenFilter, StundenForm, istOffen } from "./StundenFormen";
import { h1Zahl, monatsLabel, uhrzeit } from "./seiteHilfen";

export function StundenBlock({ projektId }: { projektId: string }) {
  const { data: zeilen = [] } = useProjektStunden(projektId);
  const { data: pauschalen = [] } = useProjektPauschalen(projektId);
  const { data: members = [] } = useTeamMembers();
  const update = useUpdateSlot(projektId);
  const del = useDeleteSlot(projektId);
  const bestaetigen = useBestaetigeStunden(projektId);
  const [editId, setEditId] = useState<string | null>(null);
  // Aufgeklappte Zeile. Die Tabelle zeigt nur, was sicher ins Fenster passt —
  // Pause, Satz, Zusatzkosten, Status und Notiz holt ein Klick auf die Zeile
  // dazu. Vorher standen alle acht Spalten nebeneinander und die Tabelle
  // musste waagerecht gescrollt werden (17.08.2026).
  const [offenId, setOffenId] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [filter, setFilter] = useState<PersonenFilter>("alle");
  const [nachtrag, setNachtrag] = useState(false);

  /** Stundensatz aus dem Teamprofil — Massstab dafuer, ob eine 0 gewollt ist. */
  const profilSatz = useMemo(
    () => new Map(members.map((m) => [m.id, m.hourly_rate || 0])),
    [members],
  );

  const anzahl = useMemo(() => ({
    alle: zeilen.length,
    helfer: zeilen.filter((z) => z.person_typ !== "inhaber").length,
    inhaber: zeilen.filter((z) => z.person_typ === "inhaber").length,
  }), [zeilen]);

  const sichtbar = useMemo(() => (
    filter === "alle" ? zeilen
      : filter === "inhaber" ? zeilen.filter((z) => z.person_typ === "inhaber")
        : zeilen.filter((z) => z.person_typ !== "inhaber")
  ), [zeilen, filter]);

  /**
   * Nach Monat gruppiert — Stundenzeilen **und** Pauschalen.
   *
   * Eine Pauschale trägt ihr Geld im `projekt_helfer_pauschale`-Eintrag, nicht
   * am Slot. Stünde sie hier nicht, zeigte der Monat 0 € an, obwohl der
   * Teilabschnitt bezahlt wurde. Sie zählt **einmal** — nicht je verknüpftem
   * Slot, denn eine Pauschale kann an mehreren Einsätzen hängen.
   *
   * Monat: das `datum` der Pauschale; fehlt es, der früheste Einsatz, der an
   * ihr hängt; sonst zu den undatierten Zeilen.
   */
  const monate = useMemo(() => {
    const m = new Map<string, { slots: StundenZeile[]; pauschalen: Pauschale[] }>();
    const fach = (ym: string) => {
      let f = m.get(ym);
      if (!f) { f = { slots: [], pauschalen: [] }; m.set(ym, f); }
      return f;
    };
    for (const z of sichtbar) fach((z.datum || "").slice(0, 7) || "ohne-datum").slots.push(z);
    if (filter !== "inhaber") {
      for (const p of pauschalen) {
        const daten = zeilen
          .filter((z) => z.pauschale_id === p.id && z.datum)
          .map((z) => z.datum)
          .sort();
        const ym = (p.datum || daten[0] || "").slice(0, 7) || "ohne-datum";
        fach(ym).pauschalen.push(p);
      }
    }
    return [...m.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [sichtbar, zeilen, pauschalen, filter]);

  const gesamt = useMemo(() => {
    const stundenkosten = sichtbar.reduce((s, z) => s + z.kosten, 0);
    const pauschalkosten = filter === "inhaber"
      ? 0
      : pauschalen.reduce((s, p) => s + (p.betrag || 0), 0);
    return {
      stunden: sichtbar.reduce((s, z) => s + z.stunden_netto, 0),
      stundenkosten,
      pauschalkosten,
      summe: stundenkosten + pauschalkosten,
      offen: sichtbar.filter(istOffen).length,
    };
  }, [sichtbar, pauschalen, filter]);

  const bestaetigeIds = (ids: string[]) => {
    if (ids.length === 0) return;
    setFehler(null);
    bestaetigen.mutate(ids, {
      onError: (e) => setFehler(e instanceof Error ? e.message : String(e)),
    });
  };

  const leer = zeilen.length === 0 && pauschalen.length === 0;

  return (
    <div className="vg-unterblock">
      <div className="vg-detail-section-title">Stunden</div>
      <p className="vg-detail-section-hint">
        Alle Einsätze des Projekts, neueste zuerst. Zeiten, Satz, Zusatzkosten und Notiz lassen sich
        hier ändern, fehlende Einsätze nachtragen und geplante auf bestätigt setzen. Nur das Datum
        hängt am Termin und wird in der Planung verschoben. Pauschalen stehen im Monat ihres Datums
        und zählen einmal.
      </p>
      <div className="vg-stunden-leiste">
        {leer ? (
          <span className="vg-detail-section-hint" style={{ margin: 0 }}>
            Für dieses Projekt ist noch kein Einsatz geplant oder erfasst.
          </span>
        ) : (
          <div className="vg-segment" role="group" aria-label="Nach Person filtern">
            {FILTER_LABEL.map(({ wert, text }) => (
              <button
                key={wert}
                type="button"
                className={filter === wert ? "is-aktiv" : ""}
                onClick={() => setFilter(wert)}
              >
                {text}<span>{anzahl[wert]}</span>
              </button>
            ))}
          </div>
        )}
        <div className="vg-stunden-summe">
          {!leer && (
            <>
              {h1Zahl(gesamt.stunden)} h · Stunden {formatEur(gesamt.stundenkosten, { round: true })}
              {gesamt.pauschalkosten > 0 && <> · Pauschalen {formatEur(gesamt.pauschalkosten, { round: true })}</>}
              {" · "}<strong>{formatEur(gesamt.summe, { round: true })}</strong>
              {gesamt.offen > 0 && (
                <button
                  type="button"
                  className="vg-proj-tag is-offen"
                  style={{ marginLeft: 8 }}
                  disabled={bestaetigen.isPending}
                  onClick={() => bestaetigeIds(sichtbar.filter(istOffen).map((z) => z.slot_id))}
                  title="Alle offenen Einsätze dieser Auswahl bestätigen"
                >
                  {gesamt.offen} offen · alle bestätigen
                </button>
              )}
            </>
          )}
          {!nachtrag && (
            <button
              type="button"
              className="vg-btn vg-btn-ghost"
              style={{ marginLeft: 8 }}
              onClick={() => { setFehler(null); setNachtrag(true); }}
            >
              + Einsatz nachtragen
            </button>
          )}
        </div>
      </div>
      {nachtrag && (
        <NachtragForm
          projektId={projektId}
          onFertig={(f) => { setFehler(f); if (!f) setNachtrag(false); }}
        />
      )}
      {fehler && <div className="vg-banner is-warn">{fehler}</div>}
      {!leer && monate.length === 0 && (
        <div className="vg-detail-section-hint">Keine Einsätze für diese Auswahl.</div>
      )}
      {monate.map(([ym, fach]) => {
        const liste = fach.slots;
        const stunden = liste.reduce((s, z) => s + z.stunden_netto, 0);
        const stundenkosten = liste.reduce((s, z) => s + z.kosten, 0);
        const pauschalkosten = fach.pauschalen.reduce((s, p) => s + (p.betrag || 0), 0);
        const offeneImMonat = liste.filter(istOffen).map((z) => z.slot_id);
        return (
          <div key={ym} className="vg-monat">
            <div className="vg-monat-kopf">
              <span className="vg-monat-name">{monatsLabel(ym)}</span>
              <span className="vg-monat-summe">
                {offeneImMonat.length > 0 && (
                  <button
                    type="button"
                    className="vg-proj-tag is-offen"
                    style={{ marginRight: 8 }}
                    disabled={bestaetigen.isPending}
                    onClick={() => bestaetigeIds(offeneImMonat)}
                    title={`${offeneImMonat.length} offene Einsätze dieses Monats bestätigen`}
                  >
                    {offeneImMonat.length} offen · bestätigen
                  </button>
                )}
                {h1Zahl(stunden)} h · {formatEur(stundenkosten + pauschalkosten, { round: true })}
                {pauschalkosten > 0 && (
                  <span className="vg-monat-teil">
                    {" "}(davon {formatEur(pauschalkosten, { round: true })} pauschal)
                  </span>
                )}
              </span>
            </div>
            <div className="k-tablewrap">
            <table className="vg-tabelle vg-stundentabelle k-cardtable">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Person</th>
                  <th className="nur-breit">Von–Bis</th>
                  <th className="is-num">Std.</th>
                  <th className="is-num">Kosten</th>
                  <th className="is-aktion" />
                </tr>
              </thead>
              <tbody>
                {liste.map((z) =>
                  editId === z.slot_id ? (
                    <StundenForm
                      key={z.slot_id}
                      zeile={z}
                      busy={update.isPending}
                      onAbbrechen={() => setEditId(null)}
                      onSpeichern={(patch) => {
                        setFehler(null);
                        update.mutate(
                          { slotId: z.slot_id, patch },
                          {
                            onSuccess: () => setEditId(null),
                            onError: (e) => setFehler(e instanceof Error ? e.message : String(e)),
                          },
                        );
                      }}
                    />
                  ) : (
                    <Fragment key={z.slot_id}>
                      <tr
                        className={offenId === z.slot_id ? "vg-zeile-klapp is-offen" : "vg-zeile-klapp"}
                        onClick={() => setOffenId(offenId === z.slot_id ? null : z.slot_id)}
                        title={offenId === z.slot_id ? "Details zuklappen" : "Details anzeigen"}
                      >
                        <td data-label="Datum">{z.datum || "—"}</td>
                        <td className="vg-zelle-person" data-label="Person">
                          <span className="vg-zelle-kurz">{z.person_name || "—"}</span>
                          {z.person_typ === "inhaber" && <span className="vg-proj-tag" style={{ marginLeft: 6 }}>Inhaber</span>}
                          {istOffen(z) && (
                            <button
                              type="button"
                              className="vg-proj-tag is-offen"
                              style={{ marginLeft: 6 }}
                              disabled={bestaetigen.isPending}
                              title="Diesen Einsatz auf bestätigt setzen"
                              onClick={(e) => { e.stopPropagation(); bestaetigeIds([z.slot_id]); }}
                            >
                              {z.confirm_status} ✓
                            </button>
                          )}
                        </td>
                        <td className="is-muted nur-breit" data-label="Von–Bis">{uhrzeit(z.start_at)}–{uhrzeit(z.end_at)}</td>
                        <td className="is-num" data-label="Std.">{h1Zahl(z.stunden_netto)}</td>
                        <td className="is-num" data-label="Kosten">
                          <KostenZelle zeile={z} profilSatz={profilSatz.get(z.person_id) ?? 0} />
                        </td>
                        <td className="is-aktion">
                          <button
                            type="button"
                            className="vg-icon-btn"
                            title="Bearbeiten"
                            onClick={(e) => { e.stopPropagation(); setEditId(z.slot_id); }}
                          >
                            ✎
                          </button>
                          <button
                            type="button"
                            className="vg-icon-btn is-danger"
                            title="Einsatz löschen"
                            onClick={(e) => { e.stopPropagation(); del.mutate(z.slot_id); }}
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                      {offenId === z.slot_id && (
                        <tr className="vg-zeile-detail">
                          <td colSpan={6}>
                            <dl>
                              <div><dt>Von–Bis</dt><dd>{uhrzeit(z.start_at)}–{uhrzeit(z.end_at)}</dd></div>
                              <div><dt>Pause</dt><dd>{h1Zahl(z.pause_stunden)} h</dd></div>
                              <div>
                                <dt>Satz</dt>
                                <dd>{z.verguetungsart === "pauschale" ? "über Pauschale" : `${formatEur(z.stundenlohn, { round: true })}/h`}</dd>
                              </div>
                              <div><dt>Zusatzkosten</dt><dd>{z.zusatzkosten ? formatEur(z.zusatzkosten, { round: true }) : "—"}</dd></div>
                              <div><dt>Status</dt><dd>{z.confirm_status || "—"}</dd></div>
                              {z.note && <div className="ist-breit"><dt>Notiz</dt><dd>{z.note}</dd></div>}
                            </dl>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ),
                )}
                {fach.pauschalen.map((p) => (
                  <tr key={p.id} className="vg-pauschal-zeile">
                    <td data-label="Datum">{p.datum || "—"}</td>
                    {/* Person und Bezeichnung teilen sich die breite Mitte: die
                        Bezeichnung ist oft ein ganzer Satz und hatte in einer
                        festen Spalte nur Platz fuer "Entsorgung, …". */}
                    <td data-label="Pauschale" colSpan={3}>
                      <span className="vg-zelle-kurz">{p.helfer_name || p.helfer_id}</span>
                      <span className="vg-proj-tag" style={{ marginLeft: 6 }}>Pauschale</span>
                      <span className="vg-pauschal-text">{p.bezeichnung || "Pauschal bezahlter Teilabschnitt"}</span>
                    </td>
                    <td className="is-num" data-label="Betrag">{formatEur(p.betrag, { round: true })}</td>
                    <td className="is-aktion" />
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
